/**
 * InspectorSidebar — right-side inspector panel with tabs
 * (Overview, Properties, History, AI), property grid, CHPROP form,
 * and MATCHPROP UI.
 * Extracted from App.tsx — pure structural refactor.
 */

import {
  type FormEvent,
  type ReactElement,
  useMemo,
  useState,
  useEffect,
} from "react";
import type { EntityPreview, PanelId } from "@dxf-ai-editor/contracts";
import {
  getEntityById,
  getEntityMetadataIndex,
  selectChpropOptions,
  selectMatchpropSourceEntity,
  selectSelectedEntities,
  useEditorStore,
  type PropertiesViewModel,
} from "../state/editorStore";
import { useCollabLockStore } from "../state/collabLockStore";
import {
  buildCollabLockPropertyRows,
  resolveEntityLockPropertySnapshots,
} from "../state/collabLockPropertyView";
import { useLanguage, useT } from "../i18n";
import { translateRawLabel } from "../i18n/dynamicBlockLabels";
import { pluralFormFor } from "../i18n/pluralForm";
import {
  listDynamicParameters,
  type DynListLine,
} from "@dxf-ai-editor/command-engine";
import {
  PropertyGridRows,
  type DynamicPropertyDescriptor,
} from "./PropertyGrid";
import {
  DynParamControl,
  type DynControlKind,
  type DynParamDescriptor,
} from "./dynamicBlockControls";
import {
  boundsForParam,
  collectRenderRows,
  compoundSuffixes,
} from "./dynamicBlockCompoundGrouping";
import { backfillBlockDynamicMetadata } from "../state/inferDynamicMetadata";
import { AiDraftingPanel } from "./aiPanel/AiDraftingPanel";
import { AiSupportPanel } from "./aiPanel/AiSupportPanel";

const panelIds: readonly PanelId[] = [
  "properties",
  "history",
  "ai",
] as const;

const panelLabelKeys: Partial<Record<PanelId, string>> = {
  properties: "panel.label.properties",
  history: "panel.label.history",
  ai: "panel.label.ai",
};

export interface InspectorSidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  propertiesView: PropertiesViewModel;
  propertyDraft: Record<string, string>;
  setPropertyDraft: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  contextEntityType: string | null;
  getStyleOptions: (label: string) => string[];
  onSubmitProperties: (event: FormEvent<HTMLFormElement>) => void;
  /** AutoCAD parity: auto-apply properties on blur/Enter/dropdown change. */
  onAutoApply: () => void;
  onOpenDimStyleEditor: () => void;
  onOpenMleaderStyleEditor: () => void;
  onOpenTextStyleEditor: () => void;
  onOpenLayerManager: () => void;
  onOpenPanel: (panel: PanelId) => void;
  /**
   * DYN-1.10 — supplies per-row dynamic descriptors (for Param: / Visibility
   * labels on dynamic-block INSERTs). Pass-through to PropertyGridRows.
   */
  getDynamicPropertyDescriptor?: (
    rowLabel: string,
  ) => DynamicPropertyDescriptor | null;
  /**
   * Phase MC5 — optional ViewportInspector panel. Rendered at the top
   * of the properties panel when exactly one Viewport entity is selected
   * in PSPACE mode. Pass null / undefined to hide.
   */
  viewportInspectorPanel?: React.ReactNode;
}

export function InspectorSidebar({
  isCollapsed,
  onToggleCollapse,
  propertiesView,
  propertyDraft,
  viewportInspectorPanel,
  setPropertyDraft,
  contextEntityType,
  getStyleOptions,
  onSubmitProperties,
  onAutoApply,
  onOpenDimStyleEditor,
  onOpenMleaderStyleEditor,
  onOpenTextStyleEditor,
  onOpenLayerManager,
  onOpenPanel,
  getDynamicPropertyDescriptor,
}: InspectorSidebarProps) {
  const t = useT();
  const { language } = useLanguage();
  const state = useEditorStore((store) => store.state);
  const dispatch = useEditorStore((store) => store.dispatch);
  // COLLAB-LOCK-PROPERTY (W3.5) — reactive subscription to the soft-lock
  // store so a lock acquired/released by ANOTHER collaborator while this
  // panel is open updates the Locked/Locked-by/Lock-acquired-at rows below
  // without requiring an unrelated editorStore change to re-render (mirrors
  // `spaceContextMode` in App.tsx — a second store's slice, read directly).
  const collabLockStatus = useCollabLockStore((store) => store.status);
  const collabLocks = useCollabLockStore((store) => store.locks);
  const collabSelfParticipantId = useCollabLockStore(
    (store) => store.selfParticipantId,
  );
  // PR-V4 — subscribe to the metadata index version so the aggregate
  // hint below `propertiesView.note` re-renders after huge imports
  // (the index itself lives outside Zustand reactive state).
  const entityMetadataIndexVersion = useEditorStore(
    (store) => store.entityMetadataIndexVersion,
  );
  // Codex P2 follow-up — also subscribe to `geometryDocument` so the
  // chprop memo below has a dep that changes when document-backed
  // entities mutate on the huge-import fallback path (where
  // `state.drawing.entities` is empty).
  const geometryDocument = useEditorStore((store) => store.geometryDocument);
  const selectedEntities = selectSelectedEntities(state);
  // Phase 1.j (П35Р React #185 fix) — `selectChpropOptions` returns a
  // freshly-allocated array on every call (it builds a derived list
  // from `selectSelectedEntities` which itself returns a new array
  // each call). The chpropOptions value was wired into the
  // `useEffect` dep array below; React saw a new reference every
  // render and re-fired the effect, which called `setChpropProperty`
  // and re-rendered, looping until React tripped the "Maximum update
  // depth exceeded" guard (#185).
  //
  // Stabilise the reference via `useMemo` keyed by the actual inputs
  // that affect the result: selection id list, reactive entities, AND
  // `geometryDocument` (because `selectSelectedEntities` falls back
  // to document-backed entities on huge imports where
  // `state.drawing.entities` is empty — see Codex P2 finding).
  const chpropOptions = useMemo(
    () => selectChpropOptions(state),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional narrow deps
    [state.selection.entityIds, state.drawing.entities, geometryDocument],
  );
  const matchpropSourceEntity = selectMatchpropSourceEntity(state);
  // PR-V4 — drawing-wide aggregates pulled from the metadata index.
  // Surfaced in the empty-selection state so the inspector reflects
  // huge-import totals even when state.drawing.entities is empty.
  const drawingAggregates = useMemo(
    () => getEntityMetadataIndex().aggregates(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- non-reactive index; bump triggers re-evaluation
    [entityMetadataIndexVersion],
  );

  // Phase MC5 iter-8 — when ViewportInspector is shown, suppress the
  // legacy property-grid rows it already owns (Scale, Display lock, Shade
  // plot) to prevent two conflicting editors for the same field. "Shade
  // plot" added in VIEWPORT-SHADEPLOT-UI: the dedicated dropdown shows the
  // real mode, so the legacy grid row below would otherwise duplicate (and,
  // before the projection fix, contradict) it.
  const filteredPropertiesView = useMemo(() => {
    if (!viewportInspectorPanel) return propertiesView;
    const suppressed = new Set(["Scale", "Display lock", "Shade plot"]);
    return {
      ...propertiesView,
      rows: propertiesView.rows.filter((row) => !suppressed.has(row.label)),
    };
  }, [viewportInspectorPanel, propertiesView]);

  // COLLAB-LOCK-PROPERTY (W3.5, MASTERPLAN H2) — append the read-only
  // Locked / Locked by / Lock acquired at rows for the current selection.
  // Gated on `collabLockStatus !== "disconnected"` (INV-OFF, mirrors
  // `collabLockVeto.ts`'s zero-overhead-when-disconnected invariant): an
  // ordinary, non-collaborating user's Properties panel stays byte-identical
  // to before this feature existed — the 3 rows only appear once a collab
  // session has actually been opened (`connecting`/`connected`/`error` all
  // surface SOMETHING — `resolveEntityLockPropertySnapshot`'s `authoritative`
  // flag degrades `connecting`/`error` to the fail-closed "Unknown" display
  // rather than a false "not locked"). Read-through only — this NEVER
  // dispatches an acquire/release; that stays the LOCK/UNLOCK CLI's job.
  //
  // local-Codex P2 finding round 4 — the EXPENSIVE part (the layer-lock
  // index build, O(locks) up to ~32k entries at the documented
  // 64-participant/512-lock scale) is computed in ITS OWN `useMemo`,
  // deliberately NOT depending on `filteredPropertiesView`. `App.tsx`'s
  // `propertiesView` prop (`derivePropertiesView(state)`, App.tsx:1422) is
  // itself un-memoized — a fresh object every App render — so
  // `filteredPropertiesView`'s OWN memo (pre-existing, ViewportInspector
  // suppression) already recomputes every render for the same reason;
  // depending on it here (round-3's fix) meant the lock-row computation
  // inherited that churn on every unrelated render (typing in the command
  // line, etc.) while connected. Splitting it out means the O(locks) scan
  // only re-runs when a TRULY lock-relevant input changes. The final
  // merge below is then a plain (non-memoized) cheap concatenation — a
  // handful of rows, negligible cost, and memoizing it would gain nothing
  // anyway (it still depends on the unstable `filteredPropertiesView`).
  const collabLockRows = useMemo(() => {
    if (
      collabLockStatus === "disconnected" ||
      state.selection.entityIds.length === 0
    ) {
      return [];
    }
    const authoritative = collabLockStatus === "connected";
    // Plural resolver — builds ONE layer-lock index shared across the whole
    // selection (local-Codex P2 finding round 1) instead of rescanning
    // `collabLocks` per selected entity.
    //
    // local-Codex P2 finding round 2 — `getEntityById` returns `undefined`
    // for a SHELL-ONLY selection (the huge-import fallback path where
    // `state.drawing.entities` was emptied past
    // `SHELL_ENTITY_PREVIEW_THRESHOLD` and the compact cache has not been
    // populated for this id yet — see `editorStoreImportFileActions.
    // collabLockVeto.test.ts`, which exercises the same shell-only state
    // against the VETO). Falling back to `entity.properties.Layer` (the
    // EntityPreview bag's canonical Layer key — every preview builder sets
    // it, `previewBuilders.ts`) means a layer-scope lock is still detected
    // for such an entity instead of silently resolving to "Locked=No" while
    // the SAME lock would veto a real edit on it.
    const targets = selectedEntities.map((entity) => ({
      entityId: entity.id,
      layerName: getEntityById(entity.id)?.layer ?? entity.properties.Layer,
    }));
    const snapshots = resolveEntityLockPropertySnapshots(
      targets,
      collabLocks,
      collabSelfParticipantId,
      authoritative,
    );
    return buildCollabLockPropertyRows(snapshots, { t, language });
    // local-Codex P2 finding round 3 — `selectedEntities` (`selectSelectedEntities`)
    // and `t` (`useT()`) are BOTH freshly allocated every render regardless of
    // whether anything relevant changed, so listing them as deps made this
    // memo recompute on EVERY unrelated App render while a collab session is
    // connected. Fixed by depending on the STABLE inputs
    // `selectSelectedEntities` itself is built from instead (mirrors this
    // file's own `chpropOptions` memo two screens up, which solves the exact
    // same freshly-allocated-array problem the same way) —
    // `state.selection.entityIds` + `state.drawing.entities` +
    // `geometryDocument` (selection identity, shell-preview data, and the
    // typed layer lookup / shell-only Layer fallback source respectively) —
    // plus `language` (`t`'s only actual input; the fresh `t` closure
    // captured in the recomputed callback above is always the CURRENT
    // render's, correctly bound to `language`, whether or not `t` itself is
    // listed here).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional narrow deps (see comment above)
  }, [
    state.selection.entityIds,
    state.drawing.entities,
    geometryDocument,
    collabLockStatus,
    collabLocks,
    collabSelfParticipantId,
    language,
  ]);

  const propertiesViewWithLockRows =
    collabLockRows.length === 0
      ? filteredPropertiesView
      : {
          ...filteredPropertiesView,
          rows: [...filteredPropertiesView.rows, ...collabLockRows],
        };

  const [chpropProperty, setChpropProperty] = useState("");
  const [chpropValue, setChpropValue] = useState("");

  useEffect(() => {
    setChpropProperty((currentProperty) =>
      chpropOptions.includes(currentProperty)
        ? currentProperty
        : (chpropOptions[0] ?? ""),
    );
  }, [chpropOptions]);

  function applyChprop(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!chpropProperty || state.selection.entityIds.length === 0) {
      return;
    }

    dispatch({
      type: "chprop/apply",
      source: "toolbar",
      entityIds: state.selection.entityIds,
      property: chpropProperty,
      value: chpropValue,
    });
    setChpropValue("");
  }

  function setMatchpropSource() {
    if (selectedEntities.length !== 1) {
      return;
    }

    dispatch({
      type: "matchprop/set-source",
      source: "toolbar",
      entityId: selectedEntities[0].id,
    });
  }

  function clearMatchpropSource() {
    dispatch({
      type: "matchprop/set-source",
      source: "toolbar",
      entityId: null,
    });
  }

  function applyMatchprop(mode: "basic" | "special" | "all") {
    if (!matchpropSourceEntity || state.selection.entityIds.length === 0) {
      return;
    }

    dispatch({
      type: "matchprop/apply",
      source: "toolbar",
      targetEntityIds: state.selection.entityIds,
      mode,
    });
  }

  return (
    <aside
      className={`side-column ${isCollapsed ? "is-collapsed" : ""}`}
      data-testid="side-column"
    >
      <button
        className={`inspector-collapse-toggle ${isCollapsed ? "is-collapsed" : ""}`}
        type="button"
        aria-expanded={!isCollapsed}
        aria-controls="inspector-card"
        data-testid="inspector-collapse-toggle"
        title={
          isCollapsed
            ? t("inspector.expand")
            : t("inspector.collapse")
        }
        onClick={onToggleCollapse}
      >
        <span aria-hidden="true">{isCollapsed ? ">" : "<"}</span>
        <span className="sr-only">
          {isCollapsed
            ? t("inspector.expand")
            : t("inspector.collapse")}
        </span>
      </button>
      {/* Always render card; CSS hides it when collapsed on desktop */}
        <article
          className={`surface-card ${isCollapsed ? "inspector-hidden" : ""}`}
          data-testid="inspector-card"
          id="inspector-card"
        >
          <div className="panel-tabs" data-testid="panel-tabs">
            {panelIds.map((id) => (
              <button
                key={id}
                className={`tab-button ${state.ui.activePanel === id ? "is-active" : ""}`}
                type="button"
                data-testid={`panel-tab-${id}`}
                onClick={() => onOpenPanel(id)}
              >
                {t(panelLabelKeys[id] as never)}
              </button>
            ))}
          </div>

          {state.ui.activePanel === "properties" ? (
            <div className="panel-stack" data-testid="properties-panel">
              <div className="section-heading">
                <div>
                  <p className="section-label">
                    {t("panel.selection_context")}
                  </p>
                  <h3>{propertiesView.title}</h3>
                </div>
                <span className="badge">{propertiesView.badge}</span>
              </div>

              <p className="panel-note" data-testid="properties-summary-note">
                {propertiesView.note}
              </p>

              {/* Phase MC5 — ViewportInspector panel for selected VIEWPORT
                  entity in PSPACE mode. Rendered above the property grid
                  when present. */}
              {viewportInspectorPanel ?? null}

              {/* PR-V4 — when nothing is selected, surface drawing-wide
                  aggregates from the metadata index so the inspector
                  shows a meaningful summary even on huge-import flows
                  where state.drawing.entities is empty. */}
              {selectedEntities.length === 0 && drawingAggregates.total > 0 ? (
                <p
                  className="panel-note"
                  data-testid="inspector-aggregate-hint"
                >
                  {(() => {
                    // Defect 2 — the RU hint used to hardcode the
                    // genitive-plural noun form for every count ("3
                    // объектОВ", "1 слоЯХ", "3 типОВ"), which is wrong
                    // Russian agreement for 1/2-4. Resolve the correct
                    // one|few|many form per count via the shared
                    // `pluralFormFor` (Intl.PluralRules), mirroring
                    // `dxfio.status.opened`'s existing noun-selection
                    // pattern.
                    const entityNounKey =
                      `dxfio.noun.entity.${pluralFormFor(drawingAggregates.total, language)}` as const;
                    const layerNounKey =
                      `inspector.noun.layer.${pluralFormFor(drawingAggregates.byLayer.size, language)}` as const;
                    const typeNounKey =
                      `inspector.noun.type.${pluralFormFor(drawingAggregates.byType.size, language)}` as const;
                    return t("inspector.aggregate_hint", {
                      total: drawingAggregates.total,
                      entityNoun: t(entityNounKey),
                      layers: drawingAggregates.byLayer.size,
                      layerNoun: t(layerNounKey),
                      types: drawingAggregates.byType.size,
                      typeNoun: t(typeNounKey),
                    });
                  })()}
                </p>
              ) : null}

              <form
                className="panel-stack"
                onSubmit={onSubmitProperties}
                data-testid="properties-form"
              >
                <div className="property-grid" data-testid="property-grid">
                  <PropertyGridRows
                    propertiesView={propertiesViewWithLockRows}
                    propertyDraft={propertyDraft}
                    setPropertyDraft={setPropertyDraft}
                    contextEntityType={contextEntityType}
                    getStyleOptions={getStyleOptions}
                    onAutoApply={onAutoApply}
                    onOpenLayerManager={onOpenLayerManager}
                    getDynamicPropertyDescriptor={getDynamicPropertyDescriptor}
                  />
                </div>
                <div className="action-row">
                  <button className="primary-button" type="submit">
                    {t("panel.apply_properties")}
                  </button>
                  <span className="microcopy">
                    {selectedEntities.length === 0
                      ? t("panel.apply_properties_hint_default")
                      : t("panel.apply_properties_hint_commit")}
                  </span>
                </div>
                {contextEntityType === "Dimension" ? (
                  <div className="action-row" style={{ marginTop: 8 }}>
                    <button
                      className="ghost-button"
                      type="button"
                      data-testid="properties-manage-dimstyles-button"
                      onClick={onOpenDimStyleEditor}
                    >
                      {t("panel.manage_dim_styles")}
                    </button>
                    <span className="microcopy">
                      {t("panel.manage_dim_styles_hint")}
                    </span>
                  </div>
                ) : null}
                {contextEntityType === "Multileader" ? (
                  <div className="action-row" style={{ marginTop: 8 }}>
                    <button
                      className="ghost-button"
                      type="button"
                      data-testid="properties-manage-mleaderstyles-button"
                      onClick={onOpenMleaderStyleEditor}
                    >
                      {t("panel.manage_mleader_styles")}
                    </button>
                    <span className="microcopy">
                      {t("panel.manage_mleader_styles_hint")}
                    </span>
                  </div>
                ) : null}
                {contextEntityType === "Text" ||
                contextEntityType === "MText" ? (
                  <div className="action-row" style={{ marginTop: 8 }}>
                    <button
                      className="ghost-button"
                      type="button"
                      data-testid="properties-manage-textstyles-button"
                      onClick={onOpenTextStyleEditor}
                    >
                      {t("panel.manage_text_styles")}
                    </button>
                    <span className="microcopy">
                      {t("panel.manage_text_styles_hint")}
                    </span>
                  </div>
                ) : null}
                <div className="action-row" style={{ marginTop: 8 }}>
                  <button
                    className="ghost-button"
                    type="button"
                    data-testid="properties-manage-layers-button"
                    onClick={onOpenLayerManager}
                  >
                    {t("panel.manage_layers")}
                  </button>
                  <span className="microcopy">
                    {t("panel.manage_layers_hint")}
                  </span>
                </div>
              </form>

              <DynamicBlockParametersSection
                selectedEntity={selectedEntities[0]}
              />

              <div className="section-subcard" data-testid="chprop-panel">
                <div className="section-heading">
                  <div>
                    <p className="section-label">{t("chprop.title")}</p>
                    <h3>{t("chprop.subtitle")}</h3>
                  </div>
                  <span className="badge">
                    {t("chprop.targets", {
                      count: selectedEntities.length,
                    })}
                  </span>
                </div>
                <form
                  className="panel-stack"
                  onSubmit={applyChprop}
                  data-testid="chprop-form"
                >
                  <div
                    className="two-column-grid"
                    data-testid="chprop-fields"
                  >
                    <label className="field-stack">
                      <span>{t("chprop.property")}</span>
                      <select
                        className="property-input"
                        data-testid="chprop-property-select"
                        value={chpropProperty}
                        onChange={(event) =>
                          setChpropProperty(event.target.value)
                        }
                      >
                        <option value="">
                          {t("chprop.select_property")}
                        </option>
                        {chpropOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field-stack">
                      <span>{t("chprop.value")}</span>
                      <input
                        className="property-input"
                        data-testid="chprop-value-input"
                        value={chpropValue}
                        onChange={(event) =>
                          setChpropValue(event.target.value)
                        }
                        placeholder={t("chprop.new_value")}
                      />
                    </label>
                  </div>
                  <div className="action-row">
                    <button
                      className="primary-button"
                      type="submit"
                      disabled={
                        !chpropProperty || selectedEntities.length === 0
                      }
                    >
                      {t("chprop.apply")}
                    </button>
                    <span className="microcopy">
                      {t("chprop.mixed_hint")}
                    </span>
                  </div>
                </form>
              </div>

              <div
                className="section-subcard"
                data-testid="matchprop-panel"
              >
                <div className="section-heading">
                  <div>
                    <p className="section-label">{t("matchprop.title")}</p>
                    <h3>{t("matchprop.subtitle")}</h3>
                  </div>
                  <span className="badge">
                    {matchpropSourceEntity
                      ? matchpropSourceEntity.label
                      : t("matchprop.no_source")}
                  </span>
                </div>
                <p className="panel-note">
                  {t("matchprop.first_slice_hint")}
                </p>
                <div className="action-row" data-testid="matchprop-actions">
                  <button
                    className="ghost-button"
                    type="button"
                    data-testid="matchprop-source-button"
                    onClick={setMatchpropSource}
                    disabled={selectedEntities.length !== 1}
                  >
                    {t("matchprop.use_selection_as_source")}
                  </button>
                  <button
                    className="ghost-button"
                    type="button"
                    data-testid="matchprop-clear-button"
                    onClick={clearMatchpropSource}
                    disabled={!matchpropSourceEntity}
                  >
                    {t("matchprop.clear_source")}
                  </button>
                </div>
                <div className="chip-row" data-testid="matchprop-modes">
                  <button
                    className="chip-button"
                    type="button"
                    data-testid="matchprop-basic-button"
                    onClick={() => applyMatchprop("basic")}
                    disabled={
                      !matchpropSourceEntity ||
                      state.selection.entityIds.length === 0
                    }
                  >
                    {t("matchprop.apply_basic")}
                  </button>
                  <button
                    className="chip-button"
                    type="button"
                    data-testid="matchprop-special-button"
                    onClick={() => applyMatchprop("special")}
                    disabled={
                      !matchpropSourceEntity ||
                      state.selection.entityIds.length === 0
                    }
                  >
                    {t("matchprop.apply_special")}
                  </button>
                  <button
                    className="chip-button"
                    type="button"
                    data-testid="matchprop-all-button"
                    onClick={() => applyMatchprop("all")}
                    disabled={
                      !matchpropSourceEntity ||
                      state.selection.entityIds.length === 0
                    }
                  >
                    {t("matchprop.apply_all")}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {state.ui.activePanel === "history" ? (
            <div className="panel-stack" data-testid="history-panel">
              {/*
                CC-1e — the non-command interaction family (commandType "ui/…":
                clicks / toggles / navs) is pure SERVER telemetry, not UI copy
                (i18n N/A). It rides the audit ring only so the CC-1a transport
                carries it; it is filtered OUT of this visible command-history
                list so the panel renders exactly as before CC-1e (no visual
                surface change, no non-localised telemetry strings leaking).
              */}
              {(() => {
                const commandAuditEvents = state.audit.events.filter(
                  (event) => !event.commandType.startsWith("ui/"),
                );
                return (
                  <>
                    <div className="section-heading">
                      <div>
                        <p className="section-label">
                          {t("audit.audit_first_shell")}
                        </p>
                        <h3>
                          {t("audit.recorded_events", {
                            count: commandAuditEvents.length,
                          })}
                        </h3>
                      </div>
                      <span className="badge">{t("audit.latest_first")}</span>
                    </div>

                    <div
                      className="history-list"
                      data-testid="audit-history-list"
                    >
                      {commandAuditEvents.map((event) => (
                        <article key={event.id} className="history-card">
                          <strong>{event.commandType}</strong>
                          <span>
                            #{event.sequence} {t("history.via")} {event.source}
                          </span>
                          <p>{event.summary}</p>
                        </article>
                      ))}
                    </div>
                  </>
                );
              })()}

              <div
                className="history-list"
                data-testid="command-transcript-list"
              >
                {state.commandLine.transcript.map((entry) => (
                  <article
                    key={entry.id}
                    className={`history-card tone-${entry.tone}`}
                  >
                    <strong>{entry.tone}</strong>
                    <p>{entry.message}</p>
                  </article>
                ))}
              </div>
            </div>
          ) : null}

          {/* CC-2d — AI panel: guardrails guidance + review-first drafting
              (ТЗ §13 AI Drafting Bot) + read-only support assistant
              (ТЗ §13 AI Support Assistant, sub-ai-support). */}
          {state.ui.activePanel === "ai" ? (
            <div className="panel-stack" data-testid="ai-panel">
              <div className="section-heading">
                <div>
                  <p className="section-label">{t("panel.label.ai")}</p>
                  <h3>{t("panel.ai_guardrails")}</h3>
                </div>
                <span className="badge">{t("ai.guardrails.badge")}</span>
              </div>
              <ul className="panel-note" data-testid="ai-guardrails-list">
                <li>{t("ai.constraint_1")}</li>
                <li>{t("ai.constraint_2")}</li>
                <li>{t("ai.constraint_3")}</li>
                <li>{t("ai.constraint_4")}</li>
              </ul>
              <AiDraftingPanel />
              <AiSupportPanel />
            </div>
          ) : null}

        </article>
    </aside>
  );
}

/**
 * Editable "Dynamic block parameters" section (Variant A — DYN-UX-A).
 *
 * Renders each customProperties entry on the selected INSERT as a
 * typed control (checkbox / select / number input with unit suffix /
 * text input). Every edit dispatches a `dynamic/set` command — the
 * same path exercised by the DYNSET CLI — so the reducer validates
 * values against typed `dynamicMetadata.parameters` before mutating
 * the immutable DrawingDocument.
 *
 * Commit strategy is encapsulated in `DynParamControl`:
 *   - checkbox / select  → immediate dispatch on change
 *   - number / text      → dispatch on blur or Enter, revert on Escape
 *
 * Fallback path (real AutoCAD output — raw-groups bridge, see 8ab89de):
 * when a block has no typed `dynamicMetadata.parameters`, the control
 * kind is inferred from the JS type of the current value. String
 * parameters in that mode render as plain text inputs (no choices are
 * available), and the section shows a "no typed metadata" hint so
 * users understand why validation is looser. The section-level
 * "Reset to defaults" button is disabled in that case because
 * DYNRESET has no typed source of truth to reset against.
 *
 * Hidden for non-Block selections and for INSERTs with no
 * customProperties — keeps the inspector uncluttered for static blocks.
 */
function DynamicBlockParametersSection({
  selectedEntity,
}: {
  selectedEntity: EntityPreview | undefined;
}): ReactElement | null {
  const t = useT();
  const { language } = useLanguage();
  const geometryDocument = useEditorStore(
    (store: { geometryDocument: ReturnType<typeof useEditorStore.getState>["geometryDocument"] }) =>
      store.geometryDocument,
  );
  const dispatch = useEditorStore(
    (store: { dispatch: ReturnType<typeof useEditorStore.getState>["dispatch"] }) =>
      store.dispatch,
  );
  if (!selectedEntity || selectedEntity.type !== "Block") return null;
  const drawingEntity = geometryDocument.spaces
    .flatMap(
      (space: (typeof geometryDocument.spaces)[number]) => space.entities,
    )
    .find(
      (entity: (typeof geometryDocument.spaces)[number]["entities"][number]) =>
        entity.id === selectedEntity.id,
    );
  if (!drawingEntity || drawingEntity.type !== "Block") return null;
  const rawBlockDef = geometryDocument.blocks[drawingEntity.blockName];
  // Phase L.1 — backfill typed `dynamicMetadata.parameters` for legacy
  // DXFs whose blocks expose only raw customProperties keys
  // (Position1 / VisibilityState1 / FlipState1 / …). When the block
  // already carries authored typed metadata `backfillBlockDynamicMetadata`
  // is a no-op identity passthrough, so this is purely additive.
  const blockDef = backfillBlockDynamicMetadata(
    rawBlockDef,
    drawingEntity.customProperties as
      | Readonly<Record<string, string | number | boolean>>
      | undefined,
  );
  const lines: DynListLine[] = listDynamicParameters(drawingEntity, blockDef);
  // Hide empty / error rows — users already see the full transcript
  // via DYNLIST; this section is purely a compact status view.
  const paramLines = lines.filter(
    (line): line is Extract<DynListLine, { kind: "param" }> =>
      line.kind === "param",
  );
  if (paramLines.length === 0) return null;

  // A block is treated as "lacking typed metadata" when `dynamicMetadata`
  // is absent or has no parameters. DYNRESET refuses to operate in that
  // case (see `resetDynamicParameters` in dynamicBlockMutations.ts), so
  // we surface a hint and disable the Reset button.
  const hasTypedMetadata = Boolean(
    blockDef?.dynamicMetadata &&
      blockDef.dynamicMetadata.parameters.length > 0,
  );

  const targetEntityId = drawingEntity.id;

  // Group suffixed scalar rows (`<base>.x`/`<base>.y`,
  // `<base>.distance`/`<base>.angle`) into compound descriptors whose
  // kind matches the authored parameter (polar / xy / point /
  // basePoint). AutoCAD shows these as single rows with two sub-fields
  // — matching that here is the Priority-#1 parity fix the raw
  // suffixed-row fallback was violating.
  const renderRows = collectRenderRows(paramLines, blockDef);

  function commit(paramName: string, rawValue: string): void {
    // Phase N.4 — route Inspector parameter edits through the
    // representation-override command. The value lands on BOTH the
    // typed `representationOverrides.parameterOverrides` map (for DXF
    // round-trip persistence as AcDbBlockRepresentationData) AND the
    // runtime `customProperties` bag (for the evaluator). Two INSERTs
    // of the same dynamic block can therefore carry independent
    // parameter values without polluting the shared BlockDefinition.
    dispatch({
      type: "geometry/set-insert-parameter-override",
      source: "scene",
      entityId: targetEntityId,
      paramName,
      rawValue,
    });
  }

  /**
   * Commit handler for compound widgets — splits the widget's combined
   * `"a,b"` rawValue back into two `dynamic/set` dispatches, one per
   * suffix consumed by `applyInstanceParameters`. The suffixes depend
   * on the compound kind (polar → .distance/.angle; xy/point/basepoint
   * → .x/.y). Skips a dispatch for a half whose value is unchanged
   * relative to the current customProperties (avoids no-op audit rows).
   */
  function commitCompound(
    base: string,
    kind: DynControlKind,
    rawValue: string,
    currentA: string,
    currentB: string,
  ): void {
    const [suffixA, suffixB] = compoundSuffixes(kind);
    const idx = rawValue.indexOf(",");
    const nextA = idx < 0 ? rawValue.trim() : rawValue.slice(0, idx).trim();
    const nextB = idx < 0 ? "" : rawValue.slice(idx + 1).trim();
    if (nextA !== currentA) commit(`${base}${suffixA}`, nextA);
    if (nextB !== currentB) commit(`${base}${suffixB}`, nextB);
  }

  function resetAll(): void {
    dispatch({
      type: "dynamic/reset",
      source: "scene",
      entityId: targetEntityId,
    });
  }

  return (
    <div
      className="section-subcard"
      data-testid="dynamic-block-parameters-panel"
    >
      <div className="section-heading">
        <div>
          <p className="section-label">
            {t("dynamic_block_params.section_label")}
          </p>
          <h3>{t("dynamic_block_params.section_title")}</h3>
        </div>
        <span className="badge">{renderRows.length}</span>
      </div>
      {!hasTypedMetadata ? (
        <p
          className="dyn-param-hint"
          data-testid="dyn-param-no-typed-metadata-hint"
        >
          {t("dynamic_block_params.no_typed_metadata")}
        </p>
      ) : null}
      <div
        className="property-grid"
        data-testid="dynamic-block-parameters-grid"
      >
        {renderRows.map((row) => {
          if (row.compound) {
            const descriptor: DynParamDescriptor = {
              name: row.base,
              kind: row.kind,
              value: `${row.rawA},${row.rawB}`,
            };
            return (
              <label
                key={row.base}
                className="property-editor-row"
                data-testid={`dyn-param-row-${row.base}`}
              >
                <span>
                  {translateRawLabel(row.base, language)}
                  <small style={{ marginLeft: 8, opacity: 0.6 }}>
                    [{row.kind}]
                  </small>
                </span>
                <DynParamControl
                  param={descriptor}
                  onCommit={(raw) =>
                    commitCompound(
                      row.base,
                      row.kind,
                      raw,
                      row.rawA,
                      row.rawB,
                    )
                  }
                  editAriaLabel={t("dynamic_block_params.edit_aria", {
                    name: row.base,
                  })}
                  unitMmLabel={t("dynamic_block_params.unit_mm")}
                  unitDegLabel={t("dynamic_block_params.unit_deg")}
                  axisXLabel={t("dynamic_block_params.axis_x")}
                  axisYLabel={t("dynamic_block_params.axis_y")}
                />
              </label>
            );
          }
          const line = row.line;
          const bounds = boundsForParam(blockDef, line.name);
          const descriptor: DynParamDescriptor = {
            name: line.name,
            kind: line.type,
            value: line.value,
            ...(line.choices && line.choices.length > 0
              ? { choices: line.choices }
              : {}),
            ...(bounds ? { bounds } : {}),
          };
          return (
            <label
              key={line.name}
              className="property-editor-row"
              data-testid={`dyn-param-row-${line.name}`}
            >
              <span>
                {translateRawLabel(line.name, language)}
                <small style={{ marginLeft: 8, opacity: 0.6 }}>
                  [{line.type}]
                </small>
              </span>
              <DynParamControl
                param={descriptor}
                onCommit={(raw) => commit(line.name, raw)}
                editAriaLabel={t("dynamic_block_params.edit_aria", {
                  name: line.name,
                })}
                unitMmLabel={t("dynamic_block_params.unit_mm")}
                unitDegLabel={t("dynamic_block_params.unit_deg")}
              />
            </label>
          );
        })}
      </div>
      <div className="action-row" style={{ marginTop: 8 }}>
        <button
          className="ghost-button"
          type="button"
          data-testid="dyn-param-reset-defaults"
          onClick={resetAll}
          disabled={!hasTypedMetadata}
          title={t("dynamic_block_params.reset_defaults_hint")}
        >
          {t("dynamic_block_params.reset_defaults")}
        </button>
        <span className="microcopy">
          {t("dynamic_block_params.reset_defaults_hint")}
        </span>
      </div>
    </div>
  );
}
